import {SurveyConditionComparatorService} from 'core/builder/SurveyConditionComparatorService'
import {Survey, SurveyElementWhatsApp} from 'core/SurveyModels'
import {SurveyBranchesService} from 'core/renderer/SurveyBranchesService'

export class SurveyNavigator {
  private trackQuestionNumber: number[] = []
  private readonly trace: number[]
  private index: number
  private readonly startingIndex
  private readonly templateStartIndex?: number
  private readonly branching!: SurveyBranchesService
  private readonly comparator = new SurveyConditionComparatorService()
  private readonly conditionedInputs: string[] = []

  /**
   * The survey property is the complete survey with merged templates in it.
   * @param survey
   * @param templateStartIndex
   */
  constructor(
    private survey: Survey,
    templateStartIndex?: number
  ) {
    this.branching = new SurveyBranchesService(this.survey)
    this.templateStartIndex = templateStartIndex ?? this.survey.pages.length
    this.conditionedInputs = this.survey.pages
      .flatMap((page) =>
        page.elements.map((el) =>
          el.condition !== undefined ? page.name + '.' + el.name : undefined
        )
      )
      .filter((i) => !!i) as string[]

    this.startingIndex = this.nextNonEmptyPageIndex(0)
    this.index = this.startingIndex
    this.trace = [this.index]
  }

  getPageIndex() {
    return this.index
  }

  getStartingPageIndex() {
    return this.startingIndex
  }

  nextNonEmptyPageIndex(index: number): number {
    if (this.survey.pages.length <= index) {
      return index
    }
    const pageName = this.survey.pages[index].name
    if (
      this.survey.pages[index].elements.filter((el) =>
        this.shouldRenderInput(pageName, el.name)
      ).length > 0
    ) {
      return index
    } else {
      return this.nextNonEmptyPageIndex(index + 1)
    }
  }

  nextPageIndex(): number {
    const l = this.survey.pages.length
    const targetPageName = this.branching.getTargetPage(
      this.survey.pages[this.index].name
    )
    if (targetPageName === 'COMPLETE') {
      this.index = l
    } else if (targetPageName === 'TEMPLATE' && this.templateStartIndex) {
      this.index = this.templateStartIndex
    } else if (targetPageName) {
      this.index = this.survey.pages.findIndex(
        (page) => page.name === targetPageName
      )
    } else {
      this.index = this.nextNonEmptyPageIndex(this.index + 1)
    }
    this.trace.push(this.index)
    this.trackQuestionNumber.push(
      this.findCountableElements(this.survey.pages[this.index - 1].name)
        ?.length || 1
    )
    return this.index
  }

  previousPageIndex(): number {
    this.trace.pop()
    this.trackQuestionNumber.pop()
    this.index = this.trace[this.trace.length - 1] ?? this.startingIndex
    return this.index
  }

  shouldRenderInput(pageName: string, elementName: string) {
    const name = `${pageName}.${elementName}`
    const element = this.findElement(pageName, elementName)
    if (element) {
      if (
        (element as SurveyElementWhatsApp)?.whatsapp &&
        element.value &&
        element.prefilled
      ) {
        // Hide this question if is marked as WhatsApp and is answered
        return false
      }
      if (this.conditionedInputs.includes(name)) {
        const target = this.findElement(
          element.condition?.target.page ?? '',
          element.condition?.target.element ?? ''
        )
        return !!(
          target &&
          this.comparator.evaluate(
            element.condition!.type,
            element.condition!.value,
            target.value
          )
        )
      }
    }
    return true
  }

  getQuestionNumber(elementName: string) {
    if (!this.survey.pages[this.index]?.elements) {
      return undefined
    }
    const page = this.survey.pages[this.index]
    const countableElements = this.findCountableElements(page.name) ?? []
    const elementLength = countableElements.length
    const elementIndex = countableElements.findIndex(
      (e) => e.name === elementName
    )
    const multiplePageElementsIndexReduction = elementLength - elementIndex
    return (
      [elementLength, ...this.trackQuestionNumber].reduce(
        (previousValue, currentValue) => previousValue + currentValue,
        1
      ) - multiplePageElementsIndexReduction
    )
  }

  private findCountableElements(pageName: string) {
    const page = this.survey.pages.find((page) => page.name === pageName)
    return page?.elements?.filter(
      (element) =>
        this.shouldRenderInput(page.name, element.name) &&
        element.type !== 'alert'
    )
  }

  private findElement(forPage: string, elementName: string) {
    const [pageIndex, elementIndex] = this.findElementIndex(
      forPage,
      elementName
    )
    if (pageIndex !== -1 && elementIndex !== -1) {
      return this.survey.pages[pageIndex].elements[elementIndex]
    }
    return undefined
  }

  private findElementIndex(forPage: string, elementName: string) {
    const pageIndex = this.findPageIndex(forPage)
    if (pageIndex === -1) {
      return [-1, -1]
    }
    return [
      pageIndex,
      this.survey.pages[pageIndex].elements.findIndex(
        (element) => element.name === elementName
      )
    ]
  }

  private findPageIndex(pageName: string) {
    return this.survey.pages.findIndex((page) => page.name === pageName)
  }
}
